import React, { useEffect } from "react";
import { Switch, useRouter } from "./router";
import { useAuth } from "./auth-context";
import NavBar from "./components/NavBar";

import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import RegisterPage from "./pages/RegisterPage";
// CourseDetailPage (the old dedicated "Enroll" page with the full chapter/
// lesson table of contents) is taken offline for now - price + Enroll now
// live directly on the homepage tiles (see HomePage.jsx), and the course
// content outline should not be visible to anyone who isn't enrolled yet.
// The file is kept, unused, in case this page comes back later.
import MyCoursesPage from "./pages/MyCoursesPage";
import CoursePlayerPage from "./pages/CoursePlayerPage";
import CertificateVerifyPage from "./pages/CertificateVerifyPage";
import MockCheckoutPage from "./pages/MockCheckoutPage";
import PurchaseSuccessPage from "./pages/PurchaseSuccessPage";
import AdminCoursesPage from "./pages/admin/AdminCoursesPage";
import AdminCourseEditPage from "./pages/admin/AdminCourseEditPage";
import AdminInquiriesPage from "./pages/admin/AdminInquiriesPage";
import AdminIssuesPage from "./pages/admin/AdminIssuesPage";
import ContactPage from "./pages/ContactPage";
import AccountSettingsPage from "./pages/AccountSettingsPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import ReportIssueButton from "./components/ReportIssueButton";

// The old course-detail/"Enroll" page is offline for now (see the import
// comment above) - anyone who still has the URL bookmarked, or a search
// engine that indexed it, gets sent back to the homepage instead of a 404 or
// a stale page that shows the content outline before enrollment.
function CourseDetailOffline() {
  const { navigate } = useRouter();
  useEffect(() => {
    navigate("/");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function RequireAuth({ children, role }) {
  const { user, loading } = useAuth();
  const { navigate } = useRouter();
  if (loading) return <div className="page"><p className="muted">Loading…</p></div>;
  if (!user) {
    navigate("/login");
    return null;
  }
  if (role && user.role !== role) {
    return (
      <div className="page">
        <div className="alert alert-error">You don't have access to this page.</div>
      </div>
    );
  }
  return children;
}

export default function App() {
  const { path } = useRouter();

  // Disable the right-click context menu site-wide, for every role
  // including admins (an earlier version exempted admins so they could
  // still use normal browser tools while managing content - that exemption
  // is very likely why right-click still appeared to work when testing
  // while logged in as the course owner/admin account, so it's been
  // removed; admins can still reach dev tools via keyboard shortcuts, e.g.
  // F12 / Ctrl+Shift+I, if they need to). This is a deterrent, not real
  // DRM - a determined visitor can still use browser dev tools, so don't
  // rely on it alone for anything sensitive.
  //
  // Belt-and-suspenders wiring, because a single document-level listener can
  // miss cases in practice:
  //  - listen on BOTH window and document, in the CAPTURE phase, so nothing
  //    upstream (a stray stopPropagation() in some element, a browser
  //    extension, etc.) can swallow the event before we see it;
  //  - also set document.oncontextmenu directly as a second, independent
  //    mechanism;
  //  - note the one thing this genuinely cannot reach: content inside a
  //    cross-frame <iframe> (YouTube/Vimeo embeds, or the browser's native
  //    PDF viewer) has its own separate document, so a contextmenu inside
  //    it never bubbles out to ours - that's a browser limitation, not a
  //    bug in this listener. See ContentBlockView.jsx for the mitigations
  //    applied there instead (toolbar/right-click hints stripped from our
  //    own PDF viewer, no "open in new tab" escape hatch for students).
  useEffect(() => {
    const blockContextMenu = (e) => {
      e.preventDefault();
      return false;
    };
    document.addEventListener("contextmenu", blockContextMenu, true);
    window.addEventListener("contextmenu", blockContextMenu, true);
    document.oncontextmenu = blockContextMenu;
    return () => {
      document.removeEventListener("contextmenu", blockContextMenu, true);
      window.removeEventListener("contextmenu", blockContextMenu, true);
      if (document.oncontextmenu === blockContextMenu) document.oncontextmenu = null;
    };
  }, []);

  const getQueryParam = (name) => new URLSearchParams(window.location.search).get(name);

  return (
    <>
      <NavBar />
      <Switch
        routes={[
          { path: "/", render: () => <HomePage /> },
          { path: "/login", render: () => <LoginPage /> },
          { path: "/register", render: () => <RegisterPage /> },
          { path: "/forgot-password", render: () => <ForgotPasswordPage /> },
          { path: "/reset-password/:token", render: ({ token }) => <ResetPasswordPage token={token} /> },
          { path: "/courses/:slug", render: () => <CourseDetailOffline /> },
          {
            path: "/dashboard",
            render: () => (
              <RequireAuth>
                <MyCoursesPage />
              </RequireAuth>
            ),
          },
          {
            path: "/learn/:slug",
            render: ({ slug }) => (
              <RequireAuth>
                <CoursePlayerPage slug={slug} />
              </RequireAuth>
            ),
          },
          { path: "/verify/:code", render: ({ code }) => <CertificateVerifyPage code={code} /> },
          { path: "/mock-checkout/:token", render: ({ token }) => <MockCheckoutPage token={token} /> },
          { path: "/purchase/success", render: () => <PurchaseSuccessPage courseSlug={getQueryParam("course")} /> },
          {
            path: "/admin",
            render: () => (
              <RequireAuth role="admin">
                <AdminCoursesPage />
              </RequireAuth>
            ),
          },
          {
            path: "/admin/courses/:id",
            render: ({ id }) => (
              <RequireAuth role="admin">
                <AdminCourseEditPage id={id} />
              </RequireAuth>
            ),
          },
          {
            path: "/admin/inquiries",
            render: () => (
              <RequireAuth role="admin">
                <AdminInquiriesPage />
              </RequireAuth>
            ),
          },
          {
            path: "/admin/issues",
            render: () => (
              <RequireAuth role="admin">
                <AdminIssuesPage />
              </RequireAuth>
            ),
          },
          { path: "/contact", render: () => <ContactPage /> },
          {
            path: "/account",
            render: () => (
              <RequireAuth>
                <AccountSettingsPage />
              </RequireAuth>
            ),
          },
        ]}
        notFound={
          <div className="page">
            <h1>Page not found</h1>
          </div>
        }
      />
      <ReportIssueButton />
    </>
  );
}
